import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, X } from 'lucide-react';

const Section = ({ title, rows, render, testId }) => (
  <div className="mt-3" data-testid={testId}>
    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{title} ({rows.length})</div>
    {rows.length === 0 ? (
      <div className="text-xs text-slate-400 italic mt-1">Nothing to do.</div>
    ) : (
      <ul className="mt-1 text-xs text-slate-700 space-y-0.5 max-h-40 overflow-y-auto">
        {rows.map((r, i) => <li key={i} className="font-mono-num">{render(r)}</li>)}
      </ul>
    )}
  </div>
);

export const RecoatCleanupButton = ({ api, onApplied }) => {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const openPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/admin/recoat-cleanup/preview');
      setPlan(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/recoat-cleanup/apply');
      const a = data.applied || {};
      toast.success(`Recoat cleanup applied · ${a.deletes} deleted · ${a.creates} created · ${a.links} linked`);
      setPlan(null);
      onApplied && onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={openPreview} disabled={busy} data-testid="recoat-cleanup-btn"
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50 transition-colors disabled:opacity-50">
        {busy && !plan ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Recoat cleanup
      </button>
      {plan && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="recoat-cleanup-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">Recoat cleanup — preview</h3>
                <p className="text-xs text-slate-500 mt-0.5">Nothing changes until you click Apply.</p>
              </div>
              <button onClick={() => setPlan(null)} data-testid="recoat-cleanup-close" className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <Section title="Delete duplicate rows" testId="recoat-cleanup-deletes" rows={plan.deletes}
              render={d => `${d.name}  →  keep "${d.keep}"  (${d.collection})`} />
            <Section title="Material recipes" testId="recoat-cleanup-recipes" rows={plan.recipes}
              render={r => `${r.recipe_name} → ${r.to_row_name}: ${r.action}`} />
            <Section title="Create missing rows" testId="recoat-cleanup-creates" rows={plan.creates}
              render={c => `${c.name}  · Mat $${c.materials_rate.toFixed(2)} + Lab $${c.labour_rate.toFixed(2)}`} />
            <Section title="Link rows to paint systems" testId="recoat-cleanup-links" rows={plan.links}
              render={l => `${l.row_name} ↔ ${l.paint_system}`} />
            <div className="mt-4 border-t border-slate-200 pt-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Report only (no changes)</div>
              <div className="text-xs text-slate-600 mt-1">
                Archived rows kept hidden: {(plan.report?.archived_rows_kept_hidden || []).join(', ') || '—'}
              </div>
              <div className="text-xs text-slate-600 mt-1" data-testid="recoat-cleanup-lowercase">
                Lowercase component types: {(plan.report?.lowercase_component_types || []).map(t => `${t.component_type} (${t.rows})`).join(', ') || '—'}
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setPlan(null)} className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
              <button onClick={apply} disabled={busy} data-testid="recoat-cleanup-apply"
                className="px-4 py-1.5 text-xs font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
                {busy && <Loader2 size={12} className="animate-spin" />} Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
